// TODO: allow canceling requests (for when a user clicks the next/back arrows repeatedly

import { datadogRum } from '@datadog/browser-rum';
import axios, { AxiosRequestConfig, AxiosResponse } from 'axios';
import { apiBase } from '@helios/frontend-core';
import { handleAchievementPolling } from '../utils/achievementPolling';
import { getImpersonatedUserId, getToken } from '@helios/frontend-auth';

export const BASE_URL = apiBase('v1');
export const BASE_URL_V2 = apiBase('v2');

const TIMEOUT = 30000; // timeout the API request after 30 seconds

const createAxiosRequest = () => {
  const axiosRequest = axios.create();

  axiosRequest.interceptors.response.use(handleAchievementPolling, async (error) => {
    if (error.response?.status === 400) {
      datadogRum.addError(error);
    }

    error.message = `${error.config?.method?.toUpperCase()} ${error.config?.url} - ${error.message}`;
    return Promise.reject(error);
  });
  return axiosRequest;
};

export function noAuthRequest({ data, method = 'get', params, url }: AxiosRequestConfig): Promise<AxiosResponse> {
  const reqUrl = `${BASE_URL}${url}`;
  return createAxiosRequest()({
    data,
    method,
    params,
    url: reqUrl,
    withCredentials: true,
  });
}

export default async function request(config: AxiosRequestConfig): Promise<AxiosResponse> {
  const { headers, method = 'get', url, data } = config;
  const impersonatedUserId = getImpersonatedUserId();
  const reqUrl = impersonatedUserId ? `${BASE_URL}/impersonation/${impersonatedUserId}${url}` : `${BASE_URL}${url}`;
  const token = getToken();

  // Ensure FormData is properly detected - don't override Content-Type if data is FormData
  // Axios automatically sets Content-Type with boundary for FormData
  const finalHeaders = { ...headers } as Record<string, string>;
  if (data instanceof FormData && finalHeaders['content-type']) {
    // Remove manually set Content-Type to let axios set it correctly with boundary
    delete finalHeaders['content-type'];
  }

  return createAxiosRequest()({
    ...config,
    headers: {
      ...finalHeaders,
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    method,
    timeout: TIMEOUT,
    url: reqUrl,
    withCredentials: true,
  });
}

export async function requestV2(config: AxiosRequestConfig): Promise<AxiosResponse> {
  const { headers, method = 'get', url } = config;
  const impersonatedUserId = getImpersonatedUserId();
  const reqUrl = `${BASE_URL_V2}${url}`;
  const token = getToken();

  if (impersonatedUserId) {
    config.params = { ...(config.params || {}), impersonatedUserId };
  }

  return createAxiosRequest()({
    ...config,
    headers: {
      ...headers,
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    method,
    timeout: TIMEOUT,
    url: reqUrl,
    withCredentials: true,
  });
}
